import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { CartService } from '../../features/ordering/data/cart.service';
import { CustomerAuthService } from '../../features/ordering/data/customer-auth.service';
import { AuthModalService } from '../../features/ordering/data/auth-modal.service';
import { I18nService } from '../i18n/i18n.service';

/**
 * Native-app style bottom tab bar, phones only (≤640px).
 *
 * WHY, when a drawer already exists: the drawer is two taps and hides the destinations behind a burger.
 * On a phone the four things a shopper actually moves between — browse, basket, orders, account — should
 * be one thumb-reach tap, which is the whole point of the Android/iOS pattern. The drawer stays for the
 * long tail (language, text size, install, transactions, rewards); this bar is the primary spine.
 *
 * SIGNED-OUT gets three tabs, not five greyed-out ones. Orders/Favourites are auth-guarded routes, so
 * offering them to a guest just bounces them to a login screen with no explanation — instead the third
 * tab IS "Login", which is what they need to do to unlock the other two.
 *
 * The bar is `position: fixed`, so the layout adds matching bottom padding to <main> at the same
 * breakpoint; without that the last element of every page sits underneath it.
 */
@Component({
  selector: 'app-bottom-nav',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive],
  template: `
    <nav class="bn" [attr.aria-label]="i18n.t('nav.customerNav')">
      <a class="bn__tab" routerLink="/discover" routerLinkActive="is-active">
        <span class="bn__ico" aria-hidden="true">🏬</span>
        <span class="bn__lbl">{{ i18n.t('nav.shops') }}</span>
      </a>

      @if (auth.isAuthenticated()) {
        <a class="bn__tab" routerLink="/favourites" routerLinkActive="is-active">
          <span class="bn__ico" aria-hidden="true">♥</span>
          <span class="bn__lbl">{{ i18n.t('nav.favourites') }}</span>
        </a>
      }

      <a class="bn__tab" routerLink="/cart" routerLinkActive="is-active">
        <span class="bn__ico" aria-hidden="true">🛒</span>
        @if (cart.count() > 0) { <span class="bn__badge">{{ cart.count() }}</span> }
        <span class="bn__lbl">{{ i18n.t('nav.cart') }}</span>
      </a>

      @if (auth.isAuthenticated()) {
        <a class="bn__tab" routerLink="/orders" routerLinkActive="is-active">
          <span class="bn__ico" aria-hidden="true">🧾</span>
          <span class="bn__lbl">{{ i18n.t('nav.orders') }}</span>
        </a>
        <a class="bn__tab" routerLink="/account" routerLinkActive="is-active">
          <span class="bn__ico" aria-hidden="true">👤</span>
          <span class="bn__lbl">{{ i18n.t('nav.account') }}</span>
        </a>
      } @else {
        <!-- A button, not a route: the auth MODAL keeps the shopper on the page they were browsing,
             whereas /login navigates away and loses it. -->
        <button type="button" class="bn__tab" (click)="authModal.open(null, 'login')">
          <span class="bn__ico" aria-hidden="true">👤</span>
          <span class="bn__lbl">{{ i18n.t('nav.login') }}</span>
        </button>
      }
    </nav>
  `,
  styles: [`
    /* Hidden by default and revealed on phones — the opposite of the usual pattern, but it means a
       desktop build never paints a fixed bar it then has to hide. */
    :host { display: none; }

    @media (max-width: 640px) {
      :host { display: block; }

      .bn {
        position: fixed;
        inset-inline: 0;
        bottom: 0;
        z-index: 90;               /* above page content, below the arrival banner (120) and drawer (61+) */
        display: flex;
        align-items: stretch;
        background: #fff;
        border-top: 1px solid #f0e6e6;
        /* iOS home-indicator inset. Falls back to 0 everywhere else, so it costs Android nothing. */
        padding-bottom: env(safe-area-inset-bottom, 0px);
        box-shadow: 0 -2px 14px rgba(17, 17, 17, 0.08);
      }

      .bn__tab {
        position: relative;
        flex: 1 1 0;
        min-width: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 0.1rem;
        /* 56px total — the platform minimum for a tab bar, and comfortably over the 40px touch floor. */
        min-height: 56px;
        padding: 0.35rem 0.15rem;
        border: none;
        background: none;
        cursor: pointer;
        font: inherit;
        text-decoration: none;
        color: var(--color-text-muted, #6b6b6b);
      }
      .bn__tab.is-active { color: var(--color-primary, #FA0E0E); }
      /* A top edge rather than a filled pill: it reads as a tab indicator and survives the icon being
         an emoji, which cannot be recoloured. */
      .bn__tab.is-active::before {
        content: '';
        position: absolute;
        inset-inline: 18%;
        top: 0;
        height: 2px;
        border-radius: 0 0 2px 2px;
        background: var(--color-primary, #FA0E0E);
      }

      .bn__ico { font-size: 1.15rem; line-height: 1; }
      .bn__lbl {
        font-size: 0.62rem;
        font-weight: 700;
        letter-spacing: 0.01em;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      /* Urdu labels are taller and need a touch more room than a 0.62rem Latin cap height. */
      :host-context([dir="rtl"]) .bn__lbl { font-size: 0.66rem; }

      .bn__badge {
        position: absolute;
        top: 0.25rem;
        inset-inline-end: 22%;
        min-width: 16px;
        height: 16px;
        padding: 0 4px;
        border-radius: 999px;
        background: var(--color-primary, #FA0E0E);
        color: #fff;
        font-size: 0.6rem;
        font-weight: 800;
        display: grid;
        place-items: center;
        /* Digits must not mirror inside an RTL bar. */
        direction: ltr;
        unicode-bidi: isolate;
      }
    }
  `],
})
export class BottomNavComponent {
  protected readonly cart = inject(CartService);
  protected readonly auth = inject(CustomerAuthService);
  protected readonly authModal = inject(AuthModalService);
  protected readonly i18n = inject(I18nService);
}
